import type { UploadFile } from "../../../provider/documents";
import type { DocumentInputs } from "../../../screens/flows/case-intake/upload-documents/types";
import { type FieldData, type FlowConfig, type FlowStep, type FlowStepBase } from "../common";

// The base types that are used for the flow data.
export type CaseIntakeFieldData = FieldData | UploadFile[];
export type FlowData = Record<string, CaseIntakeFieldData>;
export type UploadDocumentsStep = FlowStepBase & {
  type: "UPLOAD_DOCUMENTS";
  fields: {
    name: string;
  }[];
};
export type AdditionalDetailsStep = FlowStepBase & {
  type: "ADDITIONAL_DETAILS";
  header: {
    title: string;
    description?: string;
  };
  field: {
    name: "additionalDetails";
    required: true;
    placeholder: string;
  };
};
export type ReviewStep = FlowStepBase & {
  type: "REVIEW";
};
export type CaseIntakeFlowStep = FlowStep | AdditionalDetailsStep | UploadDocumentsStep | ReviewStep;
export type CaseIntakeFlowStepType = CaseIntakeFlowStep["type"];
export type CaseType = "BILL_ISSUE" | "BENEFITS_QUESTION" | "OUT_OF_NETWORK_CLAIM" | "FIND_PROVIDER" | "PLAN_ASSESSMENT" | "PRIOR_AUTHORIZATION_DENIAL" | "APP_ISSUE" | "OTHER";
export type BillDisputeType = "CHECK_BILL" | "BILL_DENIAL" | "BILL_HIGH_BALANCE" | "BILL_CHECK_COVERAGE" | "BILL_FINANCIAL_ASSISTANCE" | "BILL_COLLECTIONS" | "BILL_OTHER";
export type BenefitsQuestionType = "BENEFITS_CHECK_COVERAGE" | "BENEFITS_COST" | "BENEFITS_ASSESSMENT" | "BENEFITS_OTHER";
export const caseTypeTitleMap: Record<CaseType, string | undefined> = {
  BILL_ISSUE: "Help with a bill",
  BENEFITS_QUESTION: "Check insurance coverage",
  OUT_OF_NETWORK_CLAIM: "Submit OON claim",
  FIND_PROVIDER: "Find a provider",
  PLAN_ASSESSMENT: "Choose insurance plan",
  PRIOR_AUTHORIZATION_DENIAL: "Prior authorization denial",
  APP_ISSUE: "App Issue",
  OTHER: "How can we help you today?"
};
export const caseTypeMessageMap: Record<CaseType, string | undefined> = {
  BILL_ISSUE: "I need help with a bill",
  BENEFITS_QUESTION: "I need help checking my insurance coverage",
  OUT_OF_NETWORK_CLAIM: "I need help submitting an out of network claim.",
  FIND_PROVIDER: "I need help finding a provider.",
  PLAN_ASSESSMENT: "Help me choose a new insurance plan.",
  PRIOR_AUTHORIZATION_DENIAL: "I need help with a prior authorization denial.",
  APP_ISSUE: "I am having a technical issue with the app.",
  OTHER: undefined
};

// BILL_ISSUE Suffixes
export const billDisputeMessageMap: Record<BillDisputeType, string | undefined> = {
  CHECK_BILL: " - check if my bill is correct.",
  BILL_DENIAL: " - my insurance denied my care.",
  BILL_HIGH_BALANCE: " - my bill is unexpectedly high.",
  BILL_CHECK_COVERAGE: ".",
  BILL_FINANCIAL_ASSISTANCE: ".",
  BILL_COLLECTIONS: ".",
  BILL_OTHER: "."
};
export const billDisputeDescriptionMap: Record<BillDisputeType, string> = {
  CHECK_BILL: "Check if my bill is correct.",
  BILL_DENIAL: "My insurance denied my care.",
  BILL_HIGH_BALANCE: "My bill is unexpectedly high.",
  BILL_CHECK_COVERAGE: "I want to know if my insurance should cover this bill.",
  BILL_FINANCIAL_ASSISTANCE: "I cannot afford this bill.",
  BILL_COLLECTIONS: "I need help with a bill in collections.",
  BILL_OTHER: "I have a different issue."
};
export const additionalDetailsDescriptionMap: Partial<Record<CaseType | BillDisputeType | BenefitsQuestionType, string>> = {
  CHECK_BILL: "Please provide details about any concerns you have regarding the bill.",
  BILL_DENIAL: "Please provide details about any concerns you have regarding the bill.",
  BILL_HIGH_BALANCE: "Please provide details about any concerns you have regarding the bill.",
  BILL_CHECK_COVERAGE: "Please provide details about any concerns you have regarding the bill.",
  BILL_FINANCIAL_ASSISTANCE: "Please provide details about any concerns you have regarding the bill.",
  BILL_COLLECTIONS: "Please provide details about any concerns you have regarding the bill.",
  BILL_OTHER: "Please provide details about any concerns you have regarding the bill.",
  BENEFITS_CHECK_COVERAGE: "Please describe the procedure, appointment or medication that you would like to check is covered.",
  BENEFITS_COST: "Please describe the procedure, appointment or medication that we can help you price check.",
  BENEFITS_ASSESSMENT: "Please add additional information on specific benefits you are interested in learning about.",
  BENEFITS_OTHER: "Please describe how we can help you understand your insurance coverage.",
  OUT_OF_NETWORK_CLAIM: "Please add any other relevant details about your out of network claim.",
  FIND_PROVIDER: "Please add more details on the type of provider you are looking for.",
  PLAN_ASSESSMENT: "Please add more details on the type of insurance plan you are looking for.",
  PRIOR_AUTHORIZATION_DENIAL: "Please add any further information that may help us appeal this denial. For example: medications or procedures previously tried, and their side effects.",
  APP_ISSUE: "Please describe the issue that you encountered with the Granted app.",
  OTHER: "Please describe what we can help you with."
};
export const additionalDetailsPlaceholderMap: Partial<Record<CaseType | BillDisputeType | BenefitsQuestionType, string>> = {
  CHECK_BILL: "Add more details here.",
  BILL_DENIAL: "Add more details here.",
  BILL_HIGH_BALANCE: "Add more details here.",
  BILL_CHECK_COVERAGE: "Add more details here.",
  BILL_FINANCIAL_ASSISTANCE: "Add more details here.",
  BILL_COLLECTIONS: "Add more details here.",
  BILL_OTHER: "Add more details here.",
  BENEFITS_CHECK_COVERAGE: "“Is my physical therapy appointment covered by my insurance?”\n“How many preventive care visits do I get for free?”",
  BENEFITS_COST: "For example:\n“I need to see a physical therapist for rehabilitation. How much will this cost?”\n“How much will it cost to visit an in-network provider vs. out of network?”",
  BENEFITS_ASSESSMENT: "For example:\n“I want to start seeing a therapist for my mental health visits. Will that be covered?”\n“What kind of visits are completely covered that I should be taking advantage of?”",
  BENEFITS_OTHER: "Add more details here.",
  OUT_OF_NETWORK_CLAIM: "Add more details here.",
  FIND_PROVIDER: "For example:\nReason for the appointment\nDesired medical specialty\nIn network or out of network\nPreferred healthcare facility\nPreference for a male or female provider\nAny special accommodations needed",
  PLAN_ASSESSMENT: "Add more details here.",
  PRIOR_AUTHORIZATION_DENIAL: "For example:\n“I already tried Wegovy, but had migraine side effects.”",
  APP_ISSUE: "For example:\n“I tried to connect my insurance, but ran into an error.”",
  OTHER: "Add more details here."
};

// BENEFITS_QUESTION Suffixes
export const benefitsQuestionsMessageMap: Record<BenefitsQuestionType, string | undefined> = {
  BENEFITS_CHECK_COVERAGE: " - Is this covered under my insurance?",
  BENEFITS_COST: " - Check how much this will cost.",
  BENEFITS_ASSESSMENT: " - What benefits are included?",
  BENEFITS_OTHER: " - I have a different question."
};
export const benefitsQuestionsDescriptionMap: Record<BenefitsQuestionType, string> = {
  BENEFITS_CHECK_COVERAGE: "Check what is covered under my insurance.",
  BENEFITS_COST: "Check how much a specific service will cost.",
  BENEFITS_ASSESSMENT: "What benefits are included?",
  BENEFITS_OTHER: "I have a different question."
};
export interface CaseIntakeFlowConfig extends FlowConfig<CaseIntakeFlowStep> {}

// TODO used for testing and fallback, will be removed
export const initialFlowConfigPhase1 = (): CaseIntakeFlowConfig => {
  return {
    config: {
      initial: "case-type",
      basePath: "/case-intake",
      exit: "/home"
    },
    steps: {
      "case-type": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: false
        },
        header: {
          title: "How can we help?"
        },
        on: {
          next: {
            goto: "decision"
          }
        },
        field: {
          name: "caseType",
          required: true,
          options: [{
            id: "BILL_ISSUE",
            icon: "Receipt",
            label: "Help with a bill"
          }, {
            id: "BENEFITS_QUESTION",
            icon: "ListChecks",
            label: "Check my insurance coverage"
          }, {
            id: "OUT_OF_NETWORK_CLAIM",
            icon: "Globe",
            label: "Submit an out of network claim"
          }, {
            id: "FIND_PROVIDER",
            icon: "Search",
            label: "Find a provider"
          }, {
            id: "PLAN_ASSESSMENT",
            icon: "ShieldCheck",
            label: "Choose a new insurance plan"
          }, {
            id: "PRIOR_AUTHORIZATION_DENIAL",
            icon: "XCircle",
            label: "Prior authorization denial"
          }, {
            id: "APP_ISSUE",
            icon: "Headset",
            label: "Help with the Granted app"
          }, {
            id: "OTHER",
            icon: "HelpCircle",
            label: "Something else"
          }]
        }
      },
      "bill-issue": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        header: {
          title: "Help with a bill"
        },
        on: {
          next: {
            goto: "submit"
          }
        },
        field: {
          name: "billIssue",
          required: true,
          options: [{
            id: "CHECK_BILL",
            label: "Check if my bill is correct"
          }, {
            id: "BILL_DENIAL",
            label: "My insurance denied my care"
          }, {
            id: "BILL_HIGH_BALANCE",
            label: "My bill is unexpectedly high"
          }, {
            id: "BILL_CHECK_COVERAGE",
            label: "Should my insurance cover more?"
          }, {
            id: "BILL_COLLECTIONS",
            label: "I need help with a bill in collections"
          }, {
            id: "BILL_OTHER",
            label: "I have a different issue"
          }]
        }
      },
      "benefits-question": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        header: {
          title: "Check my insurance coverage"
        },
        on: {
          next: {
            goto: "submit"
          }
        },
        field: {
          name: "benefitsQuestion",
          required: true,
          options: [{
            id: "BENEFITS_CHECK_COVERAGE",
            label: "Is this covered under my insurance?"
          }, {
            id: "BENEFITS_COST",
            label: "Check how much this will cost"
          }, {
            id: "BENEFITS_ASSESSMENT",
            label: "What benefits are included?"
          }, {
            id: "BENEFITS_OTHER",
            label: "I have a different question"
          }]
        }
      }
    }
  };
};
export const initialFlowConfig = (): CaseIntakeFlowConfig => {
  return {
    config: {
      initial: "case-type",
      basePath: "/case-intake",
      exit: "/home"
    },
    steps: {
      "case-type": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: false
        },
        header: {
          title: "How can we help?"
        },
        on: {
          next: {
            goto: "decision"
          }
        },
        field: {
          name: "caseType",
          required: true,
          options: [{
            id: "BILL_ISSUE",
            icon: "Receipt",
            label: "Help with a bill"
          }, {
            id: "BENEFITS_QUESTION",
            icon: "ListChecks",
            label: "Check my insurance coverage"
          }, {
            id: "OUT_OF_NETWORK_CLAIM",
            icon: "Globe",
            label: "Submit an out of network claim"
          }, {
            id: "FIND_PROVIDER",
            icon: "Search",
            label: "Find a provider"
          }, {
            id: "PLAN_ASSESSMENT",
            icon: "ShieldCheck",
            label: "Choose a new insurance plan"
          }, {
            id: "PRIOR_AUTHORIZATION_DENIAL",
            icon: "XCircle",
            label: "Prior authorization denial"
          }, {
            id: "APP_ISSUE",
            icon: "Headset",
            label: "Help with the Granted app"
          }, {
            id: "OTHER",
            icon: "HelpCircle",
            label: "Something else"
          }]
        }
      },
      "bill-issue": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        header: {
          title: "Help with a bill"
        },
        on: {
          next: {
            goto: "decision"
          }
        },
        field: {
          name: "billIssue",
          required: true,
          options: [{
            id: "CHECK_BILL",
            label: "Check if my bill is correct"
          }, {
            id: "BILL_DENIAL",
            label: "My insurance denied my care"
          }, {
            id: "BILL_HIGH_BALANCE",
            label: "My bill is unexpectedly high"
          }, {
            id: "BILL_FINANCIAL_ASSISTANCE",
            label: "I cannot afford this bill"
          }, {
            id: "BILL_CHECK_COVERAGE",
            label: "Should my insurance cover more?"
          }, {
            id: "BILL_COLLECTIONS",
            label: "I need help with a bill in collections"
          }, {
            id: "BILL_OTHER",
            label: "I have a different issue"
          }]
        }
      },
      "benefits-question": {
        type: "LIST_SELECT",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        header: {
          title: "Check my insurance coverage"
        },
        on: {
          next: {
            goto: "decision"
          }
        },
        field: {
          name: "benefitsQuestion",
          required: true,
          options: [{
            id: "BENEFITS_CHECK_COVERAGE",
            label: "Is this covered under my insurance?"
          }, {
            id: "BENEFITS_COST",
            label: "Check how much this will cost"
          }, {
            id: "BENEFITS_ASSESSMENT",
            label: "What benefits are included?"
          }, {
            id: "BENEFITS_OTHER",
            label: "I have a different question"
          }]
        }
      },
      "person-select": {
        type: "PERSON_SELECT",
        header: {
          title: "Select a person",
          description: "To proceed, we need to confirm some details about the patient."
        },
        field: {
          name: "personId",
          required: true
        },
        options: {
          onExists: "skip",
          canGoBack: true
        },
        on: {
          next: {
            goto: "decision"
          }
        }
      },
      "person-name": {
        type: "PERSON_NAME",
        header: {
          title: "Enter the patient's name",
          description: "We need this information to identify the patient in our system."
        },
        fields: [{
          name: "givenName",
          required: false
        }, {
          name: "familyName",
          required: false
        }],
        options: {
          onExists: "skip",
          canGoBack: true
        },
        on: {
          next: {
            goto: "person-dob"
          }
        }
      },
      "person-dob": {
        type: "PERSON_DOB",
        header: {
          title: "Enter the patient's date of birth",
          description: "We need this information to identify the patient in our system."
        },
        field: {
          name: "personDob",
          required: false
        },
        options: {
          onExists: "skip",
          canGoBack: true
        },
        on: {
          next: {
            goto: "additional-details"
          }
        }
      },
      "upload-documents": {
        type: "UPLOAD_DOCUMENTS",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        fields: (Object.keys({} as DocumentInputs) as Array<keyof DocumentInputs>).map(key => ({
          name: key
        })),
        on: {
          next: {
            goto: "decision"
          }
        }
      },
      "additional-details": {
        type: "ADDITIONAL_DETAILS",
        options: {
          onExists: "skip",
          canGoBack: true
        },
        on: {
          next: {
            goto: "review"
          },
          skip: {
            goto: "review"
          }
        },
        header: {
          title: "Additional Details",
          description: "Please provide any additional information you need help with."
        },
        field: {
          name: "additionalDetails",
          required: true,
          placeholder: "Add more details here"
        }
      },
      review: {
        type: "REVIEW",
        options: {
          onExists: "confirm",
          canGoBack: true
        },
        on: {
          next: {
            goto: "submit"
          }
        }
      }
    }
  };
};